import React, {useEffect} from 'react';
import {useLocation} from 'react-router-dom';

import {
  useFetchProjectTriggerExecutionsQuery,
  useFetchProjectTriggersQuery,
} from '../../../generated/graphql';
import {Analytics} from '../../../services/analytics';
import {propagateErrorsContext} from '../../../util/errors';
import {TriggerExecutionsTable} from './TriggerExecutionsTable/TriggerExecutionsTable';
import {ZeroTriggerExecutions} from './ZeroTriggerExecutions';

type AutomationsHistoryTabProps = {
  entityName: string;
  projectName: string;
};

export const AutomationsHistoryTab = ({
  entityName,
  projectName,
}: AutomationsHistoryTabProps) => {
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const triggerId = urlParams.get('triggerId');

  // Analytics: fire once on arrival
  useEffect(() => {
    Analytics.track('Automations History Tab Viewed', {
      type: 'page_view',
      page: 'automations_history',
      location: location.pathname,
      entityName,
      projectName,
    });
    // Only track on initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // TODO: Prepare filters for the query
  const filters = undefined; // placeholder for now

  // Fetch triggers and trigger executions
  const projectTriggersQuery = useFetchProjectTriggersQuery({
    variables: {projectName, entityName, projectOnly: false},
    context: propagateErrorsContext(),
  });

  const projectTriggerExecutionsQuery = useFetchProjectTriggerExecutionsQuery({
    variables: {
      entityName,
      projectName,
      triggerID: triggerId,
      projectOnly: false,
      first: 30,
      order: '-created_at',
      filters,
    },
    context: propagateErrorsContext(),
  });

  // If we're here, at least one trigger should exist. However, if there are no executions, show the zero state.
  const executions =
    projectTriggerExecutionsQuery.data?.project?.triggerExecutions?.edges ?? [];

  if (executions.length === 0 && !projectTriggerExecutionsQuery.loading) {
    return <ZeroTriggerExecutions />;
  }

  // Otherwise show the table
  return (
    <TriggerExecutionsTable
      projectTriggersQuery={projectTriggersQuery}
      projectTriggerExecutionsQuery={projectTriggerExecutionsQuery}
    />
  );
};
